import { useState } from 'react';
import axiosInstance from 'interceptor/axiosInstance';
import { ApiRoutes } from 'routes/routeConstants/apiRoutes';
import { useNavigate, generatePath } from 'react-router-dom';
import { AppRoutes } from 'routes/routeConstants/appRoutes';
import { deserialize, serialize } from 'serializr';
import { ProductDistributionData } from 'models/ProductDistributionData/ProductDistributionData.model';
import { MultipleOriginsProductRawMaterial } from 'models/MultipleOriginsProductRawMaterial/multipleOriginsProductRawMaterial.model';
import { ProductTransportation } from 'models/ProductTransportation/productTransportation.model';
import Notification from 'shared/components/Notification';
import { NotificationTypes } from 'enums/NotificationEnums';
import { EmissionParams } from 'services/DashboardService/dashboard.service';
import { contentDispositionExtraction } from 'shared/utils/contentDispositionExtraction';
import { EmissionEachStage } from 'models/EmissionEachStage/emissionEachStage.model';


const ProductsService = () => {
  const navigate = useNavigate();
  const [buttonLoading, setButtonLoading] = useState<boolean>(false);
  const [draftButtonLoading, setDraftButtonLoading] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [emissionEachStageLoading, setEmissionEachStageLoading] = useState<boolean>(false);
  const [emissionEachStage, setEmissionEachStage] = useState<EmissionEachStage>();
  /** Create Product Distribution */
  const createProductDistribution = async (
    id: number,
    payload: ProductDistributionData
  ) => {
    try {
      setButtonLoading(true);
      const url = generatePath(ApiRoutes.PRODUCT_DISTRIBUTION, { id });
      const serializedPayload = serialize(ProductDistributionData, payload);
      await axiosInstance.post(url, serializedPayload);
      navigate(generatePath(AppRoutes.PRODUCT_OVERVIEW, { id }), {
        state: { inCreationPhase: true },
      });
    } catch (error) {
    } finally {
      setButtonLoading(false);
    }
  };

  /** Create Product Flow: Stage1->Save as Draft */
  const saveAsDraftCreateRawMaterial = async (
    id: number,
    rawMaterial: MultipleOriginsProductRawMaterial,
    params?: { save_draft: boolean }
  ) => {
    try {
      setDraftButtonLoading(true);
      const url = generatePath(ApiRoutes.CREATE_PRODUCT_STAGE_1, { id });
      const desiredPayload = serialize(
        MultipleOriginsProductRawMaterial,
        rawMaterial
      );
      const response = await axiosInstance.post(url, desiredPayload, {
        params,
      });
      navigate(`${AppRoutes.PRODUCT}/${AppRoutes.DRAFT}`);
      return response?.data?.message;
    } catch (error) {
    } finally {
      setDraftButtonLoading(false);
    }
  };

  /** Edit Create Product Flow: Stage1->Save as Draft */
  const saveAsDraftUpdateRawMaterial = async (
    id: number,
    rawMaterial: MultipleOriginsProductRawMaterial,
    params?: { save_draft: boolean }
  ) => {
    try {
      setDraftButtonLoading(true);
      const url = generatePath(ApiRoutes.EDIT_STAGE1, { id });
      const desiredData = serialize(
        MultipleOriginsProductRawMaterial,
        rawMaterial
      );
      const response = await axiosInstance.put(url, desiredData, { params });
      navigate(`${AppRoutes.PRODUCT}/${AppRoutes.DRAFT}`);
      return response?.data?.message;
    } catch (error) {
    } finally {
      setDraftButtonLoading(false);
    }
  };

  /** Edit Create Product Flow: Stage1->Save as Draft */
  const saveAsDraftUpdateTransportation = async (
    id: number,
    transportation: ProductTransportation,
    params?: { save_draft: boolean }
  ) => {
    try {
      setDraftButtonLoading(true);
      const url = generatePath(ApiRoutes.EDIT_STAGE2, { id });
      const desiredData = serialize(ProductTransportation, transportation);
      await axiosInstance.put(url, desiredData, { params });
      navigate(`${AppRoutes.PRODUCT}/${AppRoutes.DRAFT}`);
    } catch (error) {
    } finally {
      setDraftButtonLoading(false);
    }
  };

  /** Edit Create Product Flow: Stage4->Save as Draft */
  const saveAsDraftCreateProductDistribution = async (
    id: number,
    payload: ProductDistributionData,
    params?: { save_draft: boolean }
  ) => {
    try {
      setDraftButtonLoading(true);
      const url = generatePath(ApiRoutes.PRODUCT_DISTRIBUTION, { id });
      const serializedPayload = serialize(ProductDistributionData, payload);
      await axiosInstance.post(url, serializedPayload, { params });
      navigate(`${AppRoutes.PRODUCT}/${AppRoutes.DRAFT}`);
    } catch (error) {
    } finally {
      setDraftButtonLoading(false);
    }
  };

  /** Edit Create Product Flow: Stage4->Save as Draft */
  const saveAsDraftUpdateProductDistribution = async (
    id: number,
    payload: ProductDistributionData,
    params?: { save_draft: boolean }
  ) => {
    try {
      setDraftButtonLoading(true);
      const url = generatePath(ApiRoutes.PRODUCT_DISTRIBUTION, { id });
      const serializedPayload = serialize(ProductDistributionData, payload);
      await axiosInstance.put(url, serializedPayload, { params });
      navigate(`${AppRoutes.PRODUCT}/${AppRoutes.DRAFT}`);
    } catch (error) {
    } finally {
      setDraftButtonLoading(false);
    }
  };

  /** Edit Create Product Flow: Stage2 */
  const updateTransportation = async (
    id: number,
    transportation: ProductTransportation
  ) => {
    try {
      setButtonLoading(true);
      const url = generatePath(ApiRoutes.EDIT_STAGE2, { id });
      const desiredData = serialize(ProductTransportation, transportation);
      await axiosInstance.put(url, desiredData);
      navigate(
        generatePath(
          `${AppRoutes.CREATE_PRODUCT}/${AppRoutes.CREATE_PRODUCT_PROCESSING_FACILITY}`,
          { id: id }
        )
      );
    } catch (error) {
    } finally {
      setButtonLoading(false);
    }
  };

  /** Edit Create Product Flow: Stage4 */
  const updateProductDistribution = async (
    id: number,
    payload: ProductDistributionData
  ) => {
    try {
      setButtonLoading(true);
      const url = generatePath(ApiRoutes.EDIT_STAGE4, { id });
      const serializedPayload = serialize(ProductDistributionData, payload);
      await axiosInstance.put(url, serializedPayload);
      navigate(generatePath(AppRoutes.PRODUCT_OVERVIEW, { id }));
    } catch (error) {
    } finally {
      setButtonLoading(false);
    }
  };
  /* fetch emission for each stage */
  const fetchEmissionEachStage = async (
    id: number,
    businessModal: string,
    in_percentage?: boolean
  ) => {
    try {
      setEmissionEachStageLoading(true);
      const url = generatePath(ApiRoutes.FETCH_EMISSION_EACH_STAGE, { id });
      const response = await axiosInstance.get(url, {
        params: { business_model: businessModal.toLowerCase(), in_percentage },
      });
      const data = deserialize(
        EmissionEachStage,
        response.data
      ) as EmissionEachStage;
      setEmissionEachStage(data);
    } catch (error) {
    } finally {
      setEmissionEachStageLoading(false);
    }
  };

  // Request Ingredient
  const requestIngredient = async (payload?: {
    name: string;
    origin: string;
    material_type: string;
    additional_details?: string;
    activity_mapping_id?: string;
    product_id?: string;
    location_id?: string
  }) => {
    try {
      setButtonLoading(true);
      await axiosInstance.post(ApiRoutes.MATERIAL_REQUEST, payload);
      Notification({
        message: 'Your request has been raised successfully',
        description: '',
        type: NotificationTypes.SUCCESS,
      });
    } catch (error) {
    } finally {
      setButtonLoading(false);
    }
  };

  const downloadCSVForEachLifeCycle = async (
    productId: number,
    params?: EmissionParams
  ) => {
    try {
      setEmissionEachStageLoading(true);
      const url = generatePath(ApiRoutes.DOWNLOAD_CSV_FOR_EACH_LIFE_CYCLE, {
        productId,
      });
      const response = await axiosInstance.get(url, { params });
      const blob = new Blob([response.data]);
      const fileName = contentDispositionExtraction(response);
      const fileUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = fileUrl;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(fileUrl);
    } catch (error) {
    } finally {
      setEmissionEachStageLoading(false);
    }
  };

  const downloadbreakdownGraphCSV = async (
    url?: string,
    params?: { is_asc?: boolean }
  ) => {
    try {
      setLoading(true);
      const downloadUrl = generatePath(url + ApiRoutes.DOWNLOAD);
      const response = await axiosInstance.get(downloadUrl, { params });
      const blob = new Blob([response.data]);
      const fileName = contentDispositionExtraction(response);
      const fileUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = fileUrl;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(fileUrl);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  return {
    buttonLoading,
    loading,
    draftButtonLoading,
    requestIngredient,
    createProductDistribution,
    updateTransportation,
    updateProductDistribution,
    saveAsDraftUpdateRawMaterial,
    saveAsDraftCreateRawMaterial,
    saveAsDraftUpdateTransportation,
    saveAsDraftCreateProductDistribution,
    saveAsDraftUpdateProductDistribution,
    downloadCSVForEachLifeCycle,
    downloadbreakdownGraphCSV,
    emissionEachStageLoading,
    fetchEmissionEachStage,
    emissionEachStage,
  };
};

export default ProductsService;